/**
 * 완성보고서 승인 — 왼쪽 목록 / 오른쪽 확인 화면, 일괄 승인, 이메일 접수 등록
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, MenuItem,
  TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { Search as SearchIcon, Delete } from '@mui/icons-material';
import { api, ReportListItem, ClubCode } from '../api';
import { C, CLUBS, clubLabel, mdhm, formatPhoneInput } from '../common';
import ReportReview from './ReportReview';
import { Empty, StatusChip, ToneChip } from './ui';

const ReportsTab: React.FC<{ initialStatus?: string; batch?: string; onCountsChanged?: () => void }> = ({ initialStatus, batch, onCountsChanged }) => {
  const [status, setStatus] = useState(initialStatus || 'pending');
  const [q, setQ] = useState('');
  const [list, setList] = useState<ReportListItem[]>([]);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [checked, setChecked] = useState<string[]>([]);
  const [batchFilter, setBatchFilter] = useState(batch || '');
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [bulkResult, setBulkResult] = useState('');

  const load = () => {
    const params = new URLSearchParams({ status, q });
    if (batchFilter) params.set('batch', batchFilter);
    return api<{ data: ReportListItem[] }>(`/admin/cert/reports?${params}`).then((r) => {
      setList(r.data);
      if (!selected && r.data.length) setSelected(r.data[0]._id);
    }).catch((e) => setError(e.message));
  };
  useEffect(() => { const t = window.setTimeout(load, 250); return () => window.clearTimeout(t); }, [status, q, batchFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (batch) { setBatchFilter(batch); setStatus('all'); } }, [batch]);

  const refresh = () => { load(); onCountsChanged?.(); };
  const ready = list.filter((r) => r.ready && ['submitted', 'received', 'checking'].includes(r.status));
  const nextId = useMemo(() => {
    const i = list.findIndex((r) => r._id === selected);
    return i >= 0 && i < list.length - 1 ? list[i + 1]._id : undefined;
  }, [list, selected]);

  const readyChecked = checked.filter((id) => ready.some((r) => r._id === id));
  /** 목록에서 삭제(숨김) / 복구 — 교회 제출 현황에서도 함께 사라짐. 발급된 수료증·보관함 파일은 그대로 */
  const [confirmDel, setConfirmDel] = useState(false);
  const hideChecked = async (restore = false) => {
    if (!checked.length) return;
    setConfirmDel(false);
    await api('/admin/cert/reports/hide', { body: { ids: checked, restore } }).catch((e) => setError(e.message));
    setBulkResult(`${checked.length}건 ${restore ? '복구' : '삭제'}했습니다.`);
    setChecked([]); setSelected(null); refresh();
  };

  const bulkApprove = async () => {
    const ids = readyChecked.length ? readyChecked : ready.map((r) => r._id);
    if (!ids.length) return;
    if (!window.confirm(`확인이 끝난 ${ids.length}장을 승인하고 수료증을 발급할까요? (수료일 = 오늘)`)) return;
    const r = await api<{ data: Array<{ ok: boolean; serial?: string; message?: string }> }>('/admin/cert/reports/bulk-approve', { body: { ids } });
    const ok = r.data.filter((x) => x.ok).length;
    setBulkResult(`${ok}장 발급${r.data.length - ok ? ` · ${r.data.length - ok}장 실패 (${r.data.filter((x) => !x.ok).map((x) => x.message).join(', ')})` : ''}`);
    setChecked([]);
    refresh();
  };

  // 같은 제출 묶음끼리
  const groups = useMemo(() => {
    const map = new Map<string, ReportListItem[]>();
    list.forEach((r) => map.set(r.batchId, [...(map.get(r.batchId) || []), r]));
    return Array.from(map.entries());
  }, [list]);

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1.5 }}>
        <ToggleButtonGroup size="small" exclusive value={status} onChange={(_, v) => { if (v) { setStatus(v); setSelected(null); } }}>
          <ToggleButton value="pending">처리 대기</ToggleButton>
          <ToggleButton value="approved">발급</ToggleButton>
          <ToggleButton value="rejected">반려</ToggleButton>
          <ToggleButton value="all">전체</ToggleButton>
          <ToggleButton value="hidden">삭제됨</ToggleButton>
        </ToggleButtonGroup>
        <TextField size="small" placeholder="교회 · 등록번호 · 교사 · 담당자" value={q} onChange={(e) => setQ(e.target.value)} sx={{ flex: 1, minWidth: 220 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        {batchFilter && <Chip label={`제출 묶음 ${batchFilter}`} onDelete={() => setBatchFilter('')} />}
        <Button variant="outlined" onClick={() => setIntakeOpen(true)}>📧 이메일로 받은 보고서 등록</Button>
        {checked.length > 0 && (status === 'hidden'
          ? <Button variant="outlined" onClick={() => hideChecked(true)}>선택 {checked.length}건 복구</Button>
          : <Button variant="outlined" color="error" onClick={() => setConfirmDel(true)}>선택 {checked.length}건 목록에서 삭제</Button>)}
        {list.length > 0 && (
          <Button size="small" onClick={() => setChecked(checked.length === list.length ? [] : list.map((r) => r._id))}>
            {checked.length === list.length ? '선택 해제' : `전체 선택 (${list.length})`}
          </Button>
        )}
        {(status === 'pending' || status === 'all') && (
          <Button variant="contained" disabled={!ready.length && !readyChecked.length} onClick={bulkApprove}>
            {readyChecked.length ? `선택 ${readyChecked.length}장 승인` : `확인 완료 ${ready.length}장 일괄 승인`}
          </Button>
        )}
      </Box>
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {bulkResult && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setBulkResult('')}>{bulkResult}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '320px 1fr' }, gap: 2, alignItems: 'start' }}>
        <Box sx={{ border: `1px solid ${C.line}`, borderRadius: 2, bgcolor: '#fff', maxHeight: { md: 'calc(100vh - 230px)' }, overflowY: 'auto' }}>
          {!list.length && <Empty>{status === 'pending' ? '처리할 보고서가 없습니다 🎉' : status === 'hidden' ? '삭제한 보고서가 없습니다.' : '보고서가 없습니다.'}</Empty>}
          {groups.map(([batchId, items]) => (
            <Box key={batchId}>
              <Box sx={{ px: 1.5, py: 0.75, bgcolor: '#fffbea', borderBottom: `1px solid ${C.line}`, position: 'sticky', top: 0, zIndex: 1 }}>
                <Typography fontSize={12.5} fontWeight={800}>{items[0].churchName} <span style={{ color: C.sub, fontWeight: 600 }}>({items[0].regNo}) · {items.length}장 · {mdhm(items[0].submittedAt)}</span></Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography fontSize={11.5} color={C.sub} sx={{ flex: 1 }}>{items[0].source === 'email' ? '이메일 접수' : `담당 ${items[0].managerName}`}</Typography>
                  <Button size="small" sx={{ minWidth: 0, py: 0, fontSize: 11.5 }} onClick={() => {
                    const ids = items.map((x) => x._id);
                    const all = ids.every((id) => checked.includes(id));
                    setChecked(all ? checked.filter((x) => !ids.includes(x)) : Array.from(new Set([...checked, ...ids])));
                  }}>묶음 선택</Button>
                </Box>
              </Box>
              {items.map((r) => (
                <Box key={r._id} onClick={() => setSelected(r._id)}
                  sx={{ display: 'flex', gap: 1, alignItems: 'center', px: 1, py: 1, borderBottom: `1px solid ${C.line}`, cursor: 'pointer', bgcolor: selected === r._id ? '#fff3cf' : undefined, '&:hover': { bgcolor: '#fffbea' } }}>
                  <Checkbox size="small" checked={checked.includes(r._id)} onClick={(e) => e.stopPropagation()}
                    onChange={(e) => setChecked(e.target.checked ? [...checked, r._id] : checked.filter((x) => x !== r._id))} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                      <Typography fontWeight={800} fontSize={14}>{r.teacherName}</Typography>
                      <Typography fontSize={11.5} color={C.sub}>{clubLabel(r.club)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
                      <StatusChip status={r.status} small />
                      {r.serial && <ToneChip tone="ok" label={r.serial} />}
                      {r.requestedDate && r.status !== 'approved' && <ToneChip tone="warn" label="날짜 요청" />}
                      {['submitted', 'received', 'checking'].includes(r.status) && (r.ready ? <ToneChip tone="ok" label="확인 완료" /> : r.problems.includes('2단계 점수 미입력') && r.status === 'checking' ? <ToneChip tone="warn" label="점수 없음" /> : null)}
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          ))}
        </Box>
        <Box>{selected ? <ReportReview reportId={selected} onChanged={refresh} onNext={nextId ? () => setSelected(nextId) : undefined} /> : <Empty>왼쪽에서 보고서를 선택하세요.</Empty>}</Box>
      </Box>
      <Dialog open={confirmDel} onClose={() => setConfirmDel(false)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight={800}>선택 {checked.length}건 목록에서 삭제</DialogTitle>
        <DialogContent>
          <Box sx={{ maxHeight: 180, overflowY: 'auto', mb: 1.5, p: 1, bgcolor: '#fffbea', borderRadius: 1.5 }}>
            {list.filter((r) => checked.includes(r._id)).map((r) => (
              <Typography key={r._id} fontSize={13.5}>· {r.churchName} · <b>{r.teacherName}</b> {clubLabel(r.club)}{r.serial ? ` · ${r.serial}` : ''}</Typography>
            ))}
          </Box>
          <Typography fontSize={13.5}>관리자 목록과 <b>교회 제출 현황</b>에서 사라집니다.</Typography>
          <Typography fontSize={13.5} color={C.sub}>발급된 수료증, 교사 교육 이력, 보관함 사진은 그대로 남습니다. ‘삭제됨’에서 언제든 복구할 수 있습니다.</Typography>
        </DialogContent>
        <DialogActions><Button onClick={() => setConfirmDel(false)}>취소</Button><Button variant="contained" color="error" sx={{ background: '#b91c1c !important', color: '#fff !important' }} onClick={() => hideChecked(false)}>삭제</Button></DialogActions>
      </Dialog>
      <EmailIntakeDialog open={intakeOpen} onClose={() => setIntakeOpen(false)} onDone={refresh} />
    </Box>
  );
};

type IntakeRow = { teacherId: string; teacherName: string; club: ClubCode | ''; files: File[] };

const EmailIntakeDialog: React.FC<{ open: boolean; onClose: () => void; onDone: () => void }> = ({ open, onClose, onDone }) => {
  const [regNo, setRegNo] = useState('');
  const [church, setChurch] = useState<{ churchName: string; teacherCount: number } | null>(null);
  const [managerName, setManagerName] = useState('');
  const [managerPhone, setManagerPhone] = useState('');
  const [rows, setRows] = useState<IntakeRow[]>([{ teacherId: '', teacherName: '', club: '', files: [] }]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setChurch(null);
    if (regNo.length !== 4) return;
    api<{ data: any }>(`/church-portal/lookup?regNo=${regNo}`).then((r) => setChurch(r.data)).catch((e) => setError(e.message));
  }, [regNo]);
  const submit = async () => {
    setBusy(true); setError('');
    const form = new FormData();
    form.append('regNo', regNo);
    form.append('managerName', managerName || '이메일 접수');
    form.append('managerPhone', managerPhone);
    const items = rows.map((r, i) => ({ teacherName: r.teacherName.trim(), club: r.club, fileField: `file_${i}` }));
    rows.forEach((r, i) => r.files.forEach((f) => form.append(`file_${i}`, f)));
    form.append('items', JSON.stringify(items));
    try {
      await api('/admin/cert/reports/email-intake', { form });
      onDone(); onClose();
      setRows([{ teacherId: '', teacherName: '', club: '', files: [] }]); setRegNo('');
    } catch (e: any) { setError(e.message); } finally { setBusy(false); }
  };
  const valid = church && rows.every((r) => r.teacherName.trim() && r.club && r.files.length);
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle fontWeight={800}>이메일로 받은 완성보고서 등록 <Typography component="span" fontSize={13} color={C.sub}>report@awana.or.kr</Typography></DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField size="small" label="교회 등록번호" value={regNo} onChange={(e) => setRegNo(e.target.value.replace(/\D/g, '').slice(0, 4))} sx={{ width: 140 }} />
          {church && <Typography fontWeight={800}>{church.churchName} <span style={{ color: C.sub, fontWeight: 500 }}>· 교사 {church.teacherCount}명</span></Typography>}
          <TextField size="small" label="보낸 사람" value={managerName} onChange={(e) => setManagerName(e.target.value)} />
          <TextField size="small" label="연락처" value={managerPhone} onChange={(e) => setManagerPhone(formatPhoneInput(e.target.value))} />
        </Box>
        {rows.map((r, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
            <TextField size="small" label="교사 이름" value={r.teacherName} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, teacherName: e.target.value } : x)))} />
            <TextField select size="small" label="클럽" value={r.club} sx={{ width: 200 }} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, club: e.target.value as ClubCode } : x)))}>
              {CLUBS.map((c) => <MenuItem key={c.code} value={c.code}>{c.label}</MenuItem>)}
            </TextField>
            <Button component="label" variant="outlined" size="small">
              {r.files.length ? `파일 ${r.files.length}개` : '사진/PDF 선택'}
              <input hidden type="file" multiple accept="image/*,application/pdf" onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, files: Array.from(e.target.files || []) } : x)))} />
            </Button>
            <IconButton size="small" disabled={rows.length === 1} onClick={() => setRows(rows.filter((_, j) => j !== i))}><Delete fontSize="small" /></IconButton>
          </Box>
        ))}
        <Button size="small" onClick={() => setRows([...rows, { teacherId: '', teacherName: '', club: '', files: [] }])}>＋ 교사 추가</Button>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>닫기</Button><Button variant="contained" disabled={!valid || busy} onClick={submit}>{busy ? '올리는 중…' : `${rows.length}장 등록`}</Button></DialogActions>
    </Dialog>
  );
};

export default ReportsTab;
